import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { http } from '@/lib/api';
import { toast } from 'sonner';
import { ArrowLeft, ThumbsUp, Users, Vote } from 'lucide-react';

export default function FeatureVoting() {
  const [features, setFeatures] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    http.get('/features').then(r => { setFeatures(r.data); setLoading(false); }).catch(() => setLoading(false));
  }, []);

  const toggle = async (id) => {
    // Optimistic update
    setFeatures(prev => prev.map(f => f.id === id ? {
      ...f,
      voted_by_me: !f.voted_by_me,
      votes: f.voted_by_me ? Math.max(0, f.votes - 1) : f.votes + 1,
    } : prev.find(x => x.id === id) ? f : f));
    try {
      const { data } = await http.post(`/features/${id}/vote`);
      // Sync count from server
      setFeatures(prev => prev.map(f => f.id === id ? { ...f, votes: data.count, voted_by_me: data.voted } : f));
      toast.success(data.voted ? 'Vote counted' : 'Vote removed');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not vote');
      // Revert
      setFeatures(prev => prev.map(f => f.id === id ? { ...f, voted_by_me: !f.voted_by_me, votes: f.voted_by_me ? f.votes + 1 : Math.max(0, f.votes - 1) } : f));
    }
  };

  const maxVotes = Math.max(1, ...features.map(f => f.votes));

  return (
    <div className="p-6 lg:px-10 lg:pt-10 space-y-6 lg:space-y-8" data-testid="feature-voting-page">
      <Link to="/app" data-testid="voting-back" className="inline-flex items-center gap-2 text-sm text-navy/60 hover:text-navy">
        <ArrowLeft size={14}/> Back to home
      </Link>

      <div>
        <p className="text-xs uppercase tracking-[0.2em] text-gold font-semibold inline-flex items-center gap-1">
          <Vote size={11}/> Shape the roadmap
        </p>
        <h1 className="font-heading text-3xl lg:text-5xl mt-1 lg:mt-2">Vote on what we build next.</h1>
        <p className="hidden lg:block text-navy/60 mt-3 max-w-2xl">
          You&apos;re not just a user — you&apos;re a founding member. Tap the ideas you want to see. We build what earns the most votes.
        </p>
      </div>

      {loading ? (
        <div className="text-navy/60">Loading…</div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 lg:gap-5">
          {features.map((f) => {
            const pct = Math.round((f.votes / maxVotes) * 100);
            return (
              <div
                key={f.id}
                data-testid={`feature-card-${f.id}`}
                className={`bg-white rounded-3xl p-6 lg:p-7 card-soft flex flex-col gap-4 transition-smooth ${
                  f.voted_by_me ? 'ring-2 ring-gold/40' : ''
                }`}
              >
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-2xl bg-beige flex items-center justify-center text-2xl shrink-0">
                    {f.emoji || '✨'}
                  </div>
                  <div className="flex-1">
                    <p className="text-[10px] uppercase tracking-widest text-navy/50 font-semibold">{f.category}</p>
                    <p className="font-heading text-lg lg:text-xl text-navy mt-1 leading-snug">{f.title}</p>
                    <p className="text-sm text-navy/65 leading-relaxed mt-1">{f.description}</p>
                  </div>
                </div>

                {/* Vote bar */}
                <div className="flex items-center gap-3">
                  <div className="flex-1 h-2 bg-beige rounded-full overflow-hidden">
                    <div className="h-full bg-gold transition-all duration-500" style={{ width: `${pct}%` }}/>
                  </div>
                  <span className="text-xs font-medium text-navy/60 inline-flex items-center gap-1"><Users size={11}/> {f.votes}</span>
                </div>

                <button
                  data-testid={`feature-vote-${f.id}`}
                  onClick={() => toggle(f.id)}
                  className={`inline-flex items-center justify-center gap-2 rounded-full py-2.5 text-sm font-medium transition-smooth ${
                    f.voted_by_me
                      ? 'bg-gold text-white hover:bg-gold-hover'
                      : 'bg-beige text-navy hover:bg-[#EAE5D9]'
                  }`}
                >
                  <ThumbsUp size={13}/> {f.voted_by_me ? 'You voted' : 'Vote for this'}
                </button>
              </div>
            );
          })}
        </div>
      )}

      <p className="text-xs text-navy/40 text-center pt-2">
        Ideas we ship first are the ones the community votes for. Nothing gets built in a vacuum.
      </p>
    </div>
  );
}
